import React, { useState } from 'react';
import { Button, Input, Modal, Radio, Typography, message } from 'antd';
import { ShareAltOutlined, CopyOutlined } from '@ant-design/icons';
import cx from 'classnames';
import dayjs from 'dayjs';

import buttons from 'helpers/styles/components/buttons.module.sass';
import { createShareLink } from 'api/TimesheetShare/requests';
import { SharePeriod, TimesheetShare } from 'api/TimesheetShare/types';
import { SHARED_TIMESHEET_PAGE } from 'helpers/constants/_common/MenuCases';
import { FULL_MONTH_YEAR_FORMAT } from 'helpers/constants/_common/timeFormats';

import S from './helpers/styles.module.sass';

interface ShareMonthButtonProps {
  userId: number;
  year: number;
  month: number;
  disabled?: boolean;
}

const PERIOD_OPTIONS: { value: SharePeriod, label: string }[] = [
  { value: SharePeriod.ThreeDays, label: '3 days' },
  { value: SharePeriod.FiveDays, label: '5 days' },
  { value: SharePeriod.TwoWeeks, label: '2 weeks' },
  { value: SharePeriod.Month, label: '1 month' },
  { value: SharePeriod.ThreeMonths, label: '3 months' },
];

const buildShareUrl = (token: string) => `${window.location.origin}${SHARED_TIMESHEET_PAGE}/${token}`;

const ShareMonthButton: React.FC<ShareMonthButtonProps> = ({ userId, year, month, disabled }) => {
  const [ isModalVisible, setModalVisible ] = useState(false);
  const [ period, setPeriod ] = useState<SharePeriod>(SharePeriod.TwoWeeks);
  const [ isLoading, setLoading ] = useState(false);
  const [ share, setShare ] = useState<TimesheetShare | null>(null);

  const monthLabel = dayjs(`${year}-${month}-1`).format(FULL_MONTH_YEAR_FORMAT);

  const closeModal = () => {
    setModalVisible(false);
    // Reset so the next open starts from the period picker again.
    setShare(null);
    setPeriod(SharePeriod.TwoWeeks);
  };

  const handleCreate = () => {
    setLoading(true);

    return createShareLink({ userId, year, month, period })
      .then(setShare)
      .finally(() => setLoading(false));
  };

  const handleCopy = () => {
    if (!share) return;

    const url = buildShareUrl(share.token);

    // navigator.clipboard needs a secure context, which a plain-http instance is not.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => message.success('Link copied'));

      return;
    }

    const input = document.createElement('textarea');

    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    message.success('Link copied');
  };

  const renderPeriodStep = () => (
    <>
      <Typography.Paragraph className={S.description}>
        Anyone with the link will be able to view
        {' '}
        <b>{monthLabel}</b>
        {' '}
        in read-only mode until the link expires.
      </Typography.Paragraph>

      <Typography.Text className={S.label}>Link is valid for</Typography.Text>
      <Radio.Group
        className={S.periods}
        value={period}
        onChange={e => setPeriod(e.target.value)}
      >
        {PERIOD_OPTIONS.map(option => (
          <Radio.Button key={option.value} value={option.value}>
            {option.label}
          </Radio.Button>
        ))}
      </Radio.Group>
    </>
  );

  const renderLinkStep = () => (
    <>
      <Typography.Paragraph className={S.description}>
        Link created. Pass it on manually — anyone holding it can view the month.
      </Typography.Paragraph>

      <Typography.Text className={S.expiresNote}>
        Stops working on
        {' '}
        <b>{dayjs(share?.expirationDate).format('DD MMM YYYY, HH:mm')}</b>
      </Typography.Text>

      <Input.Group compact className={S.linkRow}>
        <Input readOnly value={share ? buildShareUrl(share.token) : ''} className={S.linkInput} />
        <Button icon={<CopyOutlined />} onClick={handleCopy}>Copy</Button>
      </Input.Group>
    </>
  );

  const renderFooter = () => {
    if (share) {
      return [ <Button key="done" type="primary" className={buttons.qsButtonPrimary} onClick={closeModal}>Done</Button> ];
    }

    return [
      <Button key="cancel" type="text" onClick={closeModal}>Cancel</Button>,
      <Button
        key="create"
        type="primary"
        loading={isLoading}
        className={buttons.qsButtonPrimary}
        onClick={handleCreate}
      >
        Create link
      </Button>,
    ];
  };

  return (
    <>
      <Button
        className={cx(buttons.qsButton, S.shareButton)}
        disabled={disabled}
        onClick={() => setModalVisible(true)}
      >
        <div className={buttons.iconTextWrapper}>
          <ShareAltOutlined />
          Share
        </div>
      </Button>

      <Modal
        title={`Share ${monthLabel}`}
        visible={isModalVisible}
        onCancel={closeModal}
        centered
        width={520}
        bodyStyle={{ padding: '24px 24px 28px' }}
        footer={renderFooter()}
      >
        { share ? renderLinkStep() : renderPeriodStep() }
      </Modal>
    </>
  );
};

export default ShareMonthButton;
